/**
 * The Online Service Fee — the ONE computation `/orders/preview`,
 * `/orders/checkout` and the public `GET /locations/slug/:slug` (which the cart
 * modal displays) share, so the total a guest is shown is the total charged.
 * Callers pass the EVALUATED platform kill switch; no browser calls this with
 * a guessed one. Spec: docs/superpowers/specs/2026-10-05-online-service-fee-design.md §2.
 *
 * Owner decision 2026-10-06 (option A): the fee applies to EVERY location that
 * takes online orders — no per-tenant flag and no owner acceptance. The legacy
 * `settings.onlineServiceFeeEnrollment` mirror is no longer read here.
 *
 * The config is client-writable (restaurant-owned), so everything here treats
 * it as untrusted input: an ABSENT field takes the default, a PRESENT but
 * malformed one never charges more than the clamp allows and never throws.
 */
import { isOnlineServiceFeeOrder, type OnlineServiceFeeLine } from '@order-up/models';

import {
  serviceChargeTaxMinor,
  serviceChargeTaxPercent,
  type ServiceChargeTaxRate,
} from '../serviceCharge/serviceChargeTax';

export const ONLINE_SERVICE_FEE_MAX_CENTS = 1000;
export const ONLINE_SERVICE_FEE_LABEL_MAX = 40;
export const ONLINE_SERVICE_FEE_DEFAULTS = {
  enabled: true,
  amountCents: 110,
  label: 'Online Service Fee',
  taxable: true,
} as const;

export interface OnlineServiceFeeTaxRate extends ServiceChargeTaxRate {
  /** `ITaxRate.default` — the rates a fee with no explicit picks is taxed at. */
  readonly default?: boolean;
}

export interface ResolvedOnlineServiceFeeConfig {
  readonly enabled: boolean;
  readonly amountCents: number;
  readonly label: string;
  readonly taxable: boolean;
  readonly taxRateIds: readonly number[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function defaultTaxRateIds(taxRates: readonly OnlineServiceFeeTaxRate[]): number[] {
  return taxRates
    .filter(
      (r) =>
        r.disabled !== true &&
        (r.type === undefined || r.type === 'Percent') &&
        r.default === true &&
        Number.isInteger(r.taxRateId),
    )
    .map((r) => r.taxRateId as number);
}

/**
 * The label prints on receipts and kitchen chits, so a control character never
 * reaches paper: whitespace runs (\n, \t, …) become one space, any other
 * C0/DEL control is removed, then the result is trimmed.
 */
function cleanLabel(raw: string): string {
  return raw
    .replace(/\s+/g, ' ')
    // eslint-disable-next-line no-control-regex -- stripping control characters is the point
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .replace(/ {2,}/g, ' ')
    .trim();
}

/** The config as the server will charge it — defaults for absent fields, clamps for the rest. */
export function resolveOnlineServiceFeeConfig(
  raw: unknown,
  taxRates: readonly OnlineServiceFeeTaxRate[],
): ResolvedOnlineServiceFeeConfig {
  const c = isRecord(raw) ? raw : {};

  let amountCents: number = ONLINE_SERVICE_FEE_DEFAULTS.amountCents;
  if (c.amountCents !== undefined) {
    amountCents =
      typeof c.amountCents === 'number' && Number.isFinite(c.amountCents)
        ? Math.min(ONLINE_SERVICE_FEE_MAX_CENTS, Math.max(0, Math.round(c.amountCents)))
        : 0; // present but garbage: never charge
  }

  const cleaned = typeof c.label === 'string' ? cleanLabel(c.label) : '';
  const label =
    cleaned.length > 0
      ? // Cut by CODE POINTS so a surrogate pair is never split into a lone half.
        Array.from(cleaned).slice(0, ONLINE_SERVICE_FEE_LABEL_MAX).join('')
      : ONLINE_SERVICE_FEE_DEFAULTS.label;

  const taxRateIds = Array.isArray(c.taxRateIds)
    ? c.taxRateIds.filter((id): id is number => Number.isInteger(id))
    : defaultTaxRateIds(taxRates);

  return {
    enabled: c.enabled === undefined ? ONLINE_SERVICE_FEE_DEFAULTS.enabled : c.enabled === true,
    amountCents,
    label,
    taxable: typeof c.taxable === 'boolean' ? c.taxable : ONLINE_SERVICE_FEE_DEFAULTS.taxable,
    taxRateIds,
  };
}

/**
 * Is the restaurant's own guest-fee switch on, as the server would charge it
 * (absent = defaults: on at $1.10)? Kill switch and order source aside — the
 * staff view and the storefront estimate read this, never a stored mirror.
 */
export function isOnlineServiceFeeConfigOn(raw: unknown): boolean {
  const config = resolveOnlineServiceFeeConfig(raw, []);
  return config.enabled && config.amountCents > 0;
}

export interface OnlineServiceFeeInput {
  /** The platform kill switch `initiative.online_service_fee` — evaluated by the caller, fail closed. */
  readonly flagOn: boolean;
  /** The location's `settings` (any shape; read defensively). */
  readonly settings?: {
    readonly feesAndCharges?: { readonly onlineServiceFee?: unknown } | null;
  } | null;
  readonly order: { readonly orderSource?: string | null; readonly externalSource?: string | null };
  /** The location's tax rates (disabled ones are skipped). */
  readonly taxRates: readonly OnlineServiceFeeTaxRate[];
  /** The order is tax-exempt: the fee is still charged, untaxed. */
  readonly exempt?: boolean;
}

/** The fee line to charge, or `null` when none applies. */
export function resolveOnlineServiceFee(input: OnlineServiceFeeInput): OnlineServiceFeeLine | null {
  if (input.flagOn !== true) return null;
  if (!isOnlineServiceFeeOrder(input.order)) return null;
  const config = resolveOnlineServiceFeeConfig(input.settings?.feesAndCharges?.onlineServiceFee, input.taxRates);
  if (!config.enabled || config.amountCents <= 0) return null;
  const percent = serviceChargeTaxPercent({
    taxable: config.taxable,
    taxRateIds: config.taxRateIds,
    taxRates: input.taxRates,
    exempt: input.exempt === true,
  });
  const taxMinor = serviceChargeTaxMinor(config.amountCents, percent);
  return { amount: config.amountCents / 100, tax: taxMinor / 100, label: config.label };
}

/** The ⓘ copy beside the fee on every guest surface. */
export function onlineServiceFeeTooltip(locationName?: string | null): string {
  const name = locationName?.trim() ? locationName.trim() : 'the restaurant';
  return `Supports ${name}'s online ordering. Not a tip and not paid to staff.`;
}
